// Telemetría ANÓNIMA de la landing + atribución de campaña (embudo de entrada).
//
// eventos_v2 exige user_id, así que el tramo landing → registro era invisible:
// no sabíamos cuántos visitantes llegan desde Instagram/web ni dónde se caen
// antes del login. Este módulo cierra ese hoyo con dos piezas:
//
// 1. Eventos anónimos (landing_vista / landing_etapa / landing_cta) vía
//    /api/track-landing → tabla eventos_anonimos. Catálogo CERRADO de eventos
//    y campos — jamás texto libre, jamás la IP (el server guarda solo
//    país/región de los headers de Vercel, igual que geo-ping).
// 2. Atribución UTM/referrer persistida en localStorage ANTES del redirect a
//    Google OAuth, para adjuntarla al signup_completado — así la conversión
//    por canal se mide de punta a punta.
//
// Kill-switch: REACT_APP_LANDING_TRACKING_ENABLED (ON por defecto, 'false'
// apaga). Solo web: en nativo no hay landing de visitante (el usuario ya
// instaló la app) y el origin capacitor:// no pasa el CORS del endpoint.

import { isNative } from './platform';
import { APP_VERSION } from '../utils/appVersion';

const API_BASE = process.env.REACT_APP_API_URL || '';
const ATTRIB_KEY = 'mipae:attrib';
const VISITOR_KEY = 'mipae:visitor_id';
const VISTA_KEY = 'mipae:landing:vista'; // sessionStorage: 1 vista por sesión de pestaña

const habilitado = () =>
  process.env.REACT_APP_LANDING_TRACKING_ENABLED !== 'false' && !isNative;

// ── Funciones puras (testeables) ─────────────────────────────────────────────

const limpiarValor = (v) => {
  if (typeof v !== 'string') return null;
  const limpio = v.trim().slice(0, 80);
  return limpio || null;
};

// Extrae la atribución de la URL de entrada. Del referrer solo el HOST (nunca
// el path: puede llevar identificadores). ownHost descarta navegación interna.
export function parseAtribucion(search, referrer, ownHost = '') {
  let params;
  try { params = new URLSearchParams(search || ''); } catch { params = new URLSearchParams(); }
  const out = {
    utm_source: limpiarValor(params.get('utm_source')),
    utm_medium: limpiarValor(params.get('utm_medium')),
    utm_campaign: limpiarValor(params.get('utm_campaign')),
    referrer_host: null,
  };
  if (referrer) {
    try {
      const host = new URL(referrer).hostname;
      if (host && host !== ownHost) out.referrer_host = host.slice(0, 120);
    } catch { /* referrer malformado: se ignora */ }
  }
  if (!out.utm_source && !out.utm_medium && !out.utm_campaign && !out.referrer_host) return null;
  return out;
}

// Combina la atribución guardada con la de esta visita. Una visita CON utm
// gana (última campaña que trajo al usuario); sin utm se conserva lo previo
// y solo se rellena el referrer si faltaba.
export function mergeAtribucion(previa, nueva) {
  if (!nueva) return previa || null;
  if (!previa) return nueva;
  const nuevaTieneUtm = !!(nueva.utm_source || nueva.utm_medium || nueva.utm_campaign);
  if (nuevaTieneUtm) {
    return {
      utm_source: nueva.utm_source,
      utm_medium: nueva.utm_medium,
      utm_campaign: nueva.utm_campaign,
      referrer_host: nueva.referrer_host || previa.referrer_host || null,
    };
  }
  return { ...previa, referrer_host: previa.referrer_host || nueva.referrer_host || null };
}

// ── Identidad anónima de dispositivo ─────────────────────────────────────────
// UUID estable en localStorage. NO es el session_id de eventos_v2 (ese es por
// pestaña); este permite contar visitantes únicos y ver recurrencia pre-signup.

function generarUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* crypto bloqueado: fallback abajo */ }
  // Fallback con forma de UUID v4 (el endpoint valida el formato).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function visitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = generarUuid();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return generarUuid(); // storage bloqueado: id efímero, igual cuenta la vista
  }
}

// ── Atribución persistida ────────────────────────────────────────────────────

export function capturarAtribucion() {
  if (isNative) return;
  try {
    const nueva = parseAtribucion(window.location.search, document.referrer, window.location.hostname);
    const previa = atribucionGuardada();
    const merged = mergeAtribucion(previa, nueva);
    if (merged) localStorage.setItem(ATTRIB_KEY, JSON.stringify(merged));
  } catch { /* best-effort: la atribución nunca rompe el arranque */ }
}

export function atribucionGuardada() {
  try {
    const raw = localStorage.getItem(ATTRIB_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Campos de atribución para el payload de signup_completado (eventos_v2).
// Devuelve {} si no hay nada — el payload no se ensucia con nulls.
export function atribucionParaPayload() {
  const a = atribucionGuardada();
  const out = {};
  if (a?.utm_source) out.utm_source = a.utm_source;
  if (a?.utm_medium) out.utm_medium = a.utm_medium;
  if (a?.utm_campaign) out.utm_campaign = a.utm_campaign;
  if (a?.referrer_host) out.referrer_host = a.referrer_host;
  try {
    const etapa = localStorage.getItem('mipae:landing:etapa');
    if (etapa) out.landing_etapa = etapa;
  } catch { /* storage bloqueado */ }
  return out;
}

// ── Eventos anónimos ─────────────────────────────────────────────────────────

// Fire-and-forget. keepalive: el click en "Iniciar sesión con Google" navega
// de inmediato al OAuth y el request debe sobrevivir a esa navegación.
export function trackLanding(evento, { etapa = null, cta = null } = {}) {
  if (!habilitado()) return;
  try {
    const a = atribucionGuardada() || {};
    fetch(`${API_BASE}/api/track-landing`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_id: visitorId(),
        evento,
        etapa,
        cta,
        utm_source: a.utm_source || null,
        utm_medium: a.utm_medium || null,
        utm_campaign: a.utm_campaign || null,
        referrer_host: a.referrer_host || null,
        app_version: APP_VERSION,
      }),
    }).catch(() => { /* telemetría best-effort */ });
  } catch { /* nunca romper la landing por telemetría */ }
}

// Vista de landing: 1× por sesión de pestaña (evita inflar con re-renders y
// vueltas del OAuth cancelado).
export function trackLandingVista(etapa = null) {
  if (!habilitado()) return;
  try {
    if (sessionStorage.getItem(VISTA_KEY)) return;
    sessionStorage.setItem(VISTA_KEY, '1');
  } catch { /* sin sessionStorage igual registramos la vista */ }
  trackLanding('landing_vista', { etapa });
}
